import { useState, useEffect } from 'react';
import LandingScreen from './landing-screen';
import BlackOwlLogin from './black-owl-login';
import HomeScreen from './home-screen';
import LobbyScreen from './lobby-screen';
import DraftScreen from './draft-screen';
import GameScreen from './game-screen';

// ============================================================
// LANDING PAGE TOGGLE
// true  = landing page shows first (production / public-facing)
// false = skip straight to login (local dev / internal testing)
// ============================================================
const LANDING_PAGE_ENABLED = true;

// ============================================================
// SESSION PERSISTENCE
// Keeps the user where they were across browser refreshes,
// phone locks, and accidental tab reloads. Without this, a
// refresh mid-draft dumps them back to the landing page.
// Bump STORAGE_KEY if the saved state shape ever changes.
// v2 (FB-14) adds isSpectator; a v1 session is read once and
// carried over as a player, so nobody is logged out by the bump.
// ============================================================
const STORAGE_KEY = 'blackowl_session_v2';
const PREVIOUS_STORAGE_KEY = 'blackowl_session_v1';

function loadSession() {
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      raw = localStorage.getItem(PREVIOUS_STORAGE_KEY);
      localStorage.removeItem(PREVIOUS_STORAGE_KEY);
    }
    if (!raw) return null;
    const s = { isSpectator: false, ...JSON.parse(raw) };

    // Lobby-dependent screens need a lobbyId; otherwise fall back to home
    if (['lobby', 'draft', 'game'].includes(s.screen) && !s.lobbyId) {
      return { ...s, screen: 'home', lobbyId: null, isHost: false, isSpectator: false };
    }
    return s;
  } catch {
    return null;
  }
}

function clearSession() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage unavailable (private mode, etc.) - non-fatal
  }
}

const saved = loadSession();

function App() {
  const [user, setUser] = useState(saved?.user ?? null);
  const [passedLanding, setPassedLanding] = useState(
    !LANDING_PAGE_ENABLED || saved?.passedLanding === true
  );
  const [screen, setScreen] = useState(saved?.screen ?? 'home');
  const [lobbyId, setLobbyId] = useState(saved?.lobbyId ?? null);
  const [isHost, setIsHost] = useState(saved?.isHost ?? false);
  const [isSpectator, setIsSpectator] = useState(saved?.isSpectator ?? false);

  // Persist navigation state whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ user, passedLanding, screen, lobbyId, isHost, isSpectator })
      );
    } catch {
      // storage unavailable - app still works, just won't survive refresh
    }
  }, [user, passedLanding, screen, lobbyId, isHost, isSpectator]);

  const goHome = () => {
    setScreen("home");
    setLobbyId(null);
    setIsHost(false);
    setIsSpectator(false);
  };

  const handleLogout = () => {
    clearSession();
    setUser(null);
    goHome();
  };

  // Landing page gate - shown first unless disabled or already passed
  if (!passedLanding) {
    return <LandingScreen onEarlyAccess={() => setPassedLanding(true)} />;
  }

  if (!user) {
    return <BlackOwlLogin onLoginSuccess={(username, lobby, paidUser) => {
      setUser(username);
      if (lobby) {
        setLobbyId(lobby);
        setScreen("lobby");
      } else {
        goHome();
      }
    }} />;
  }

  if (screen === "game" && lobbyId) {
    return (
      <GameScreen
        username={user}
        lobbyId={lobbyId}
        isSpectator={isSpectator}
        onEndGame={goHome}
      />
    );
  }

  if (screen === "draft" && lobbyId) {
    return (
      <DraftScreen
        username={user}
        lobbyId={lobbyId}
        isSpectator={isSpectator}
        onDraftComplete={() => setScreen("game")}
        onLeaveLobby={goHome}
      />
    );
  }

  if (screen === "lobby" && lobbyId) {
    return (
      <LobbyScreen
        username={user}
        lobbyId={lobbyId}
        isHost={isHost}
        isSpectator={isSpectator}
        onLeaveLobby={goHome}
        onStartDraft={() => setScreen("draft")}
      />
    );
  }

  return (
    <HomeScreen
      username={user}
      onLogout={handleLogout}
      onJoinLobby={(id) => { setLobbyId(id); setIsHost(false); setIsSpectator(false); setScreen("lobby"); }}
      onCreateLobby={(id) => { setLobbyId(id); setIsHost(true); setIsSpectator(false); setScreen("lobby"); }}
      // FB-14: the lobby screen forwards to the draft/game if already underway
      onWatchLobby={(id) => { setLobbyId(id); setIsHost(false); setIsSpectator(true); setScreen("lobby"); }}
    />
  );
}

export default App;
